export async function getProjectFairDetails(year, chapterKey) {
  const chapterQuery = chapterKey ? `?chapter=${encodeURIComponent(chapterKey)}` : '';
  return await fetch(`/api/project_fair/${year}${chapterQuery}`)
    .then((resp) => resp.json())
    .then((data) => {
      data.ApprovedProjects = Array.isArray(data.ApprovedProjects) ? data.ApprovedProjects : [];
      data.ApprovedProjects.sort(() => Math.random() - 0.5);
      return data;
    });
}

export async function getPastProjectFairs() {
  const response = await fetch(`/api/project_fair`);
  // A 401 (session expired) or the 404 this route returns for an empty archive both yield a
  // body that isn't an array, and calling flatMap on it threw "data.flatMap is not a
  // function" — a TypeError that told the operator nothing about the real problem. An empty
  // archive is a legitimate state, so report it as no projects rather than as a failure.
  if (!response.ok) {
    if (response.status === 404) return [];
    throw new Error(`Could not load the project archive (${response.status}).`);
  }
  const data = await response.json();
  if (!Array.isArray(data)) return [];

  // Every fair's published projects — no fair-level gate. Previously this only included
  // fairs whose own `active` flag was false (i.e. past fairs), which silently hid the
  // current/live fair's projects from the /projects archive. A project belongs here iff
  // it is itself published (its own `active` flag isn't false), matching the live fair
  // page (getProjectFairDetails). Return raw API order; sort is owned by the Projects
  // component (Random/Newest/Oldest).
  //
  // De-duplicate by project id. A project legitimately belongs to more than one fair
  // event — e.g. a project shown at both its chapter's in-person fair and the virtual
  // fair — and /api/project_fair returns it under each, so the flatMap emits it once per
  // event. The archive is a catalogue of projects, not of appearances, so each project
  // must appear exactly once. Beyond the visible repeats, the duplicates shared a React
  // `key` in the card list, which left stale cards in the DOM after a filter change (the
  // "filtering to 2024 also shows 2026 projects" report). Keep the first occurrence so
  // API order is otherwise preserved.
  const seen = new Set();
  return data
    .flatMap((a) => (a.ApprovedProjects ?? []).filter((project) => project.active !== false))
    .filter((project) => {
      const key = project?.id;
      if (key === undefined || key === null) return true;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

export async function getProjectFairAdminData(year, chapterId) {
  const params = new URLSearchParams();
  if (year !== undefined && year !== null && `${year}`.trim() !== '') {
    params.set('year', String(year));
  }
  if (chapterId !== undefined && chapterId !== null && `${chapterId}`.trim() !== '') {
    params.set('chapter_id', String(chapterId));
  }
  const qs = params.toString();
  const response = await fetch(`/api/project_fair/events${qs ? `?${qs}` : ''}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch project fair admin data: ${response.status}`);
  }
  return response.json();
}

export async function upsertProjectFairEvent(year, chapterId, extra) {
  const response = await fetch('/api/project_fair/events', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ year, chapter_id: chapterId, ...(extra || {}) }),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.message || `Failed to create/find event: ${response.status}`);
  }
  return response.json();
}

export async function updateProjectFairEvent(eventId, payload) {
  const response = await fetch(`/api/project_fair/events/${eventId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(`Failed to update event: ${response.status}`);
  }
  return response.json();
}

export async function updateProjectFairEventChapters(eventId, chapterIds) {
  const response = await fetch(`/api/project_fair/events/${eventId}/chapters`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ chapter_ids: chapterIds }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.message || `Failed to update event chapters: ${response.status}`);
  }
  return payload;
}

export async function deleteProjectFairEvent(eventId) {
  const response = await fetch(`/api/project_fair/events/${eventId}`, { method: 'DELETE' });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.message || `Failed to delete event: ${response.status}`);
  }
}

export async function addProjectToFairEvent(eventId, projectId) {
  const response = await fetch(`/api/project_fair/events/${eventId}/projects/${projectId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`Failed to add project to event: ${response.status}`);
  }
  return response.json();
}

export async function removeProjectFromFairEvent(eventId, projectId) {
  const response = await fetch(`/api/project_fair/events/${eventId}/projects/${projectId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`Failed to remove project from event: ${response.status}`);
  }
  return response.json();
}

export async function createProjectFairProject(eventId, payload) {
  const response = await fetch(`/api/project_fair/events/${eventId}/projects`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(`Failed to create project: ${response.status}`);
  }
  return response.json();
}

export async function importProjectFairProjects(payload) {
  const response = await fetch('/api/project_fair/events/import', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(`Failed to import project fair rows: ${response.status}`);
  }
  return response.json();
}

/** Reads a File into a base64 string (no data: prefix), for JSON transport. */
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error || new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Uploads a zip of logos for an event. The server matches what it can and returns the
 * leftovers (with their image bytes) for the operator to resolve.
 */
export async function importProjectFairLogos(eventId, zipFile, logoNames = [], parkedBatchIds = [], ignoredLogoNames = []) {
  const zip = await fileToBase64(zipFile);
  const response = await fetch(`/api/project_fair/events/${eventId}/logos`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      zip,
      logo_names: logoNames,
      parked_batch_ids: parkedBatchIds,
      ignored_logo_names: ignoredLogoNames,
    }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.message || `Failed to import logos: ${response.status}`);
  }
  return payload;
}

/** Attaches one operator-resolved logo from the import stepper to a project. */
export async function assignProjectFairLogo(eventId, projectId, { data, sourceName }) {
  const response = await fetch(`/api/project_fair/events/${eventId}/logos/${projectId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ data, source_name: sourceName }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.message || `Failed to assign logo: ${response.status}`);
  }
  return payload;
}
/**
 * Cross-chapter import batches awaiting this user's review. Officers see their own
 * chapter's queue; admins see every chapter's.
 */
export async function getParkedProjectFairImports() {
  const response = await fetch('/api/project_fair/parked-imports');
  if (!response.ok) {
    throw new Error(`Failed to load parked imports: ${response.status}`);
  }
  return response.json();
}

/** Approves a parked batch, importing its rows into the reviewing chapter's own fair. */
export async function approveParkedProjectFairImport(batchId) {
  const response = await fetch(`/api/project_fair/parked-imports/${batchId}/approve`, { method: 'POST' });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.message || `Failed to approve import: ${response.status}`);
  }
  return payload;
}

/** Declines a parked batch. The rows are retained on the record for reference. */
export async function discardParkedProjectFairImport(batchId) {
  const response = await fetch(`/api/project_fair/parked-imports/${batchId}/discard`, { method: 'POST' });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.message || `Failed to discard import: ${response.status}`);
  }
  return payload;
}
